import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Building2, ShieldCheck, PoundSterling, Clock, ArrowRight, Users, FileText, Zap, Phone } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { COMPANY } from "@/data/site";
import { createCorporateAccount } from "@/lib/api";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

const BENEFITS = [
  { Icon: FileText, title: "Monthly invoicing", desc: "Every journey consolidated into one clear monthly statement — no driver payments, no expenses admin." },
  { Icon: Zap, title: "Priority dispatch", desc: "Named accounts jump the queue. Priority routing for airport, station and last-minute business runs." },
  { Icon: PoundSterling, title: "Preferential rates", desc: "Volume-based corporate pricing with fixed fares. Transparent, predictable and easy to budget." },
  { Icon: Users, title: "Dedicated account manager", desc: "One point of contact who knows your team, your routes and your preferences." },
  { Icon: ShieldCheck, title: "Fully licensed & insured", desc: "PSV/PCV insured, ISO 9001, ISO 27001 and ISO 45001 certified — travel your compliance team will love." },
  { Icon: Clock, title: "24/7 UK dispatch", desc: "Live operators around the clock — early flights, late arrivals and after-hours events, always covered." },
];

const initial = {
  company: "", contact_name: "", position: "", email: "", phone: "",
  monthly_journeys: "", departments: "", billing_address: "", vat: "", notes: "",
};

const inputCls = "bg-surface border-transparent rounded-xl h-12 px-4 text-ink placeholder:text-ink/35 focus:bg-white focus:border-brand focus-visible:ring-2 focus-visible:ring-brand/20 transition-colors";
const labelCls = "text-[11px] font-bold text-ink/50 uppercase tracking-[0.12em] mb-2 block";

export default function BusinessAccounts() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.contact_name || !form.email) {
      toast.error("Please add your company name, contact and email.");
      return;
    }
    setLoading(true);
    try {
      await createCorporateAccount(form);
      setDone(true);
      toast.success("Application received — our accounts team will be in touch within 1 working day.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please call our accounts team on 01293 515151.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Seo
        title="Corporate & Business Accounts"
        description="Open a Clocktower Cars corporate account for your Crawley or Gatwick business — monthly invoicing, priority dispatch, executive vehicles and a dedicated account manager."
        path="/business"
      />

      {/* HERO */}
      <section className="relative bg-ink py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute -top-32 left-1/3 h-[420px] w-[820px] rounded-full bg-brand/15 blur-[130px]" />
          <div className="absolute inset-0 opacity-[0.04] bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:56px_56px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div {...fadeUp} className="lg:col-span-7">
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-brand-light mb-5 flex items-center gap-2">
              <Building2 className="h-4 w-4" /> Corporate accounts
            </p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tighter leading-[0.95]">
              Business travel,<br /><span className="text-brand-light">handled beautifully.</span>
            </h1>
            <p className="mt-6 text-lg text-white/70 max-w-xl leading-relaxed">
              Trusted by the NHS, Pfizer and dozens of local businesses across Gatwick, Crawley and the Manor Royal
              business district. Open a Clocktower corporate account for monthly-invoiced travel, priority dispatch and
              a dedicated account manager.
            </p>
            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm font-medium text-white/70">
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand-light" /> ISO 9001 · 27001 · 45001</span>
              <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-brand-light" /> 24/7 UK dispatch</span>
              <span className="flex items-center gap-2"><Users className="h-4 w-4 text-brand-light" /> 774+ reviews</span>
            </div>
          </motion.div>

          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="lg:col-span-5">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-light mb-3">Prefer to talk?</p>
              <p className="text-white/80 leading-relaxed mb-6">
                Speak with our accounts team about your travel needs — we'll design a plan around your routes,
                billing cycle and reporting.
              </p>
              <a href={`tel:${COMPANY.phoneRaw}`} className="inline-flex items-center gap-3 bg-brand hover:bg-brand-light text-white px-7 py-3.5 rounded-full font-bold transition-colors">
                <Phone className="h-4 w-4" /> Call {COMPANY.phone}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-2xl mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand mb-3">What you get</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-ink">Every detail, engineered for business travel</h2>
          </motion.div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map((b, i) => (
              <motion.div key={b.title} {...fadeUp} transition={{ ...fadeUp.transition, delay: (i % 3) * 0.06 }} data-testid={`benefit-${i}`} className="bg-surface border border-[#EAEBEA] rounded-2xl p-7">
                <span className="grid place-items-center h-12 w-12 rounded-xl bg-brand text-white mb-5"><b.Icon className="h-6 w-6" strokeWidth={1.75} /></span>
                <h3 className="font-heading text-lg font-bold text-ink mb-2">{b.title}</h3>
                <p className="text-ink/60 leading-relaxed text-sm">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FORM */}
      <section id="apply" className="bg-surface py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand mb-3">Open an account</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-ink">Apply in under two minutes</h2>
            <p className="mt-4 text-ink/60 max-w-xl mx-auto leading-relaxed">
              Fill in your details and our accounts team will contact you within one working day to complete setup.
              No card is required to apply.
            </p>
          </motion.div>

          {done ? (
            <motion.div {...fadeUp} data-testid="corporate-success" className="bg-white rounded-2xl p-10 text-center shadow-[0_24px_50px_-12px_rgba(0,0,0,0.15)]">
              <div className="mx-auto mb-5 grid place-items-center h-16 w-16 rounded-full bg-brand/10 text-brand">
                <CheckCircle2 className="h-9 w-9" strokeWidth={2} />
              </div>
              <h3 className="font-heading text-2xl font-bold text-ink mb-3">Application received</h3>
              <p className="text-ink/60 mb-6 max-w-md mx-auto leading-relaxed">
                Thank you. Our accounts team will be in touch within one working day to finalise your Clocktower
                corporate account and enable priority dispatch.
              </p>
              <Button data-testid="corporate-another-btn" onClick={() => setDone(false)} className="bg-brand hover:bg-brand-light text-white rounded-full px-8 font-bold">
                Submit another application
              </Button>
            </motion.div>
          ) : (
            <motion.form
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.05 }}
              onSubmit={submit}
              data-testid="corporate-form"
              className="bg-white rounded-2xl p-7 md:p-10 shadow-[0_24px_50px_-12px_rgba(0,0,0,0.08)]"
            >
              <div className="grid gap-5 md:grid-cols-2">
                <div>
                  <Label className={labelCls}>Company name *</Label>
                  <Input data-testid="corp-company" className={inputCls} value={form.company} onChange={set("company")} placeholder="Your company Ltd" />
                </div>
                <div>
                  <Label className={labelCls}>Contact name *</Label>
                  <Input data-testid="corp-contact-name" className={inputCls} value={form.contact_name} onChange={set("contact_name")} placeholder="Full name" />
                </div>
                <div>
                  <Label className={labelCls}>Position / role</Label>
                  <Input data-testid="corp-position" className={inputCls} value={form.position} onChange={set("position")} placeholder="Office Manager" />
                </div>
                <div>
                  <Label className={labelCls}>Work email *</Label>
                  <Input data-testid="corp-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@company.com" />
                </div>
                <div>
                  <Label className={labelCls}>Phone</Label>
                  <Input data-testid="corp-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="01293 000 000" />
                </div>
                <div>
                  <Label className={labelCls}>Expected monthly journeys</Label>
                  <Select value={form.monthly_journeys} onValueChange={set("monthly_journeys")}>
                    <SelectTrigger data-testid="corp-journeys" className={inputCls}><SelectValue placeholder="Select volume" /></SelectTrigger>
                    <SelectContent className="bg-white border-[#EAEBEA] text-ink rounded-xl">
                      <SelectItem value="Fewer than 10">Fewer than 10</SelectItem>
                      <SelectItem value="10-50">10 – 50</SelectItem>
                      <SelectItem value="50-100">50 – 100</SelectItem>
                      <SelectItem value="100-250">100 – 250</SelectItem>
                      <SelectItem value="250+">250+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label className={labelCls}>Departments / cost centres who will book</Label>
                  <Input data-testid="corp-departments" className={inputCls} value={form.departments} onChange={set("departments")} placeholder="Executive team, Sales, Operations…" />
                </div>
                <div className="md:col-span-2">
                  <Label className={labelCls}>Billing address</Label>
                  <Input data-testid="corp-billing" className={inputCls} value={form.billing_address} onChange={set("billing_address")} placeholder="Registered office address" />
                </div>
                <div>
                  <Label className={labelCls}>VAT number (optional)</Label>
                  <Input data-testid="corp-vat" className={inputCls} value={form.vat} onChange={set("vat")} placeholder="GB 000 000 000" />
                </div>
                <div className="md:col-span-2">
                  <Label className={labelCls}>Anything else we should know?</Label>
                  <Textarea data-testid="corp-notes" className="bg-surface border-transparent rounded-xl px-4 text-ink placeholder:text-ink/35 focus:bg-white focus:border-brand focus-visible:ring-2 focus-visible:ring-brand/20" value={form.notes} onChange={set("notes")} placeholder="Regular routes, preferred vehicle types, compliance requirements…" />
                </div>
              </div>

              <Button
                type="submit"
                data-testid="corp-submit"
                disabled={loading}
                className="mt-7 w-full py-4 h-auto bg-brand hover:bg-brand-light text-white rounded-full text-base font-bold transition-all duration-300 hover:shadow-[0_8px_24px_rgba(31,157,42,0.35)] active:scale-[0.98]"
              >
                {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Sending…</> : <span className="inline-flex items-center gap-2">Open my corporate account <ArrowRight className="h-4 w-4" /></span>}
              </Button>
              <p className="mt-3 text-center text-xs text-ink/40">
                We&apos;ll never share your details. By submitting you agree to our privacy policy.
              </p>
            </motion.form>
          )}
        </div>
      </section>
    </Layout>
  );
}
